import { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import {
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import io from "socket.io-client";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as React from "react";
import { UserContext } from "../../App";
import HPOTrainingStatus from "./HPOTrainingStatus";
import AFSTrainingStatus from "./AFSTrainingStatus";

export default function PipelineStatus(props) {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();
  // get current URL
  const location = useLocation();
  // get experiment methods
  const {
    storedUid,
    datasets,
    currentDatasetIdx,
    experiments,
    currentExperimentIdx,
    fetchExperiments,
  } = React.useContext(UserContext);
  const fromAnalyzer = location.state?.fromAnalyzer || false;
  // create socket connection
  const socket = io(`${process.env.REACT_APP_BACKEND_URL}`, {
    path: "/socket",
    forceNew: true,
    autoConnect: false,
    timeout: 50000,
    transports: ["websocket", "polling", "flashsocket"],
  });
  const [socketRoomData, setSocketRoomData] = React.useState({});

  const navigate = useNavigate();
  const [pipelineSteps, setPipelineSteps] = React.useState([]);
  const [stepFinishStatus, setStepFinishStatus] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [pipelineMode, setPipelineMode] = React.useState("active");
  const [stepWasUpdatedOnce, setStepWasUpdatedOnce] = React.useState(true);
  const [activeStep, setActiveStep] = React.useState(0);

  const handleFinish = () => {
    const datasetName = datasets[currentDatasetIdx]?.datasetName;
    const experimentName = experiments[currentExperimentIdx]?.experimentName;
    if (datasetName && experimentName) {
      navigate(
        `/analyze/${encodeURIComponent(datasetName)}/${encodeURIComponent(
          experimentName
        )}`
      );
      return;
    }
    navigate("/analyze");
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleStepWasUpdated = (value) => {
    setStepWasUpdatedOnce(value);
  }


  /* SOCKET CONNECTION
      The socket.io connection is used, to update the processing progress
      */

  // connect to socket, when the current dataset is gathered
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setPipelineMode(params.get("mode"))

    let runName = window.location.pathname.split("/").pop();
    const expName = decodeURIComponent(
      window.location.pathname.split(`/pipeline/${runName}`)[0].split("/").pop()
    );
    runName = decodeURIComponent(runName);
    if (expName === experiments[currentExperimentIdx].experimentName) {
      socket.connect();
      socket.on("connect", () => {
        const roomData = {
          uid: storedUid,
          datasetName: datasets[currentDatasetIdx].datasetName,
          experimentName: experiments[currentExperimentIdx].experimentName,
          runName,
        };
        setSocketRoomData(roomData);
        // this method will be triggered by the server
        socket.on("update-pipeline", (data) => {
          setPipelineSteps(data.stepsAndCompletionStatus.stepNames);
          // from all steps, get the last one in list that has been set to finished
          const finishedSteps = data.stepsAndCompletionStatus.stepStatus
            .map((status, idx) => (status === true ? idx : -1))
            .filter((idx) => idx !== -1);
          setActiveStep(
            data.stepsAndCompletionStatus.stepNames[finishedSteps.length]
          );
          // check if is the last step
          if (finishedSteps.length === data.stepsAndCompletionStatus.stepNames.length) {
            setStepWasUpdatedOnce(false)
          }
          setStepFinishStatus(data.stepsAndCompletionStatus.stepStatus);
          setIsLoading(false);
        });
        socket.on("run-completed", () => {
          socket.disconnect();
          fetchExperiments({ latest: props.experimentName });
        });

        // create room for current user
        socket.emit("create-pipeline-room", roomData);
      });
    }
  }, [
    datasets,
    currentDatasetIdx,
    experiments,
    currentExperimentIdx,
    stepWasUpdatedOnce,
    location.pathname,
  ]);

  // Helper function to determine the progress component to render
  const renderStepComponent = (step) => {
    const stepName = String(step);

    if (stepName.startsWith("HPO")) {
      return (
        <HPOTrainingStatus
          pipelineRoom={socketRoomData}
          socket={socket}
          stepName={step}
          handleStepWasUpdated={handleStepWasUpdated}
        />
      );
    }
    if (stepName.startsWith("AFS")) {
      return (
        <AFSTrainingStatus
          pipelineRoom={socketRoomData}
          socket={socket}
          stepName={step}
          handleStepWasUpdated={handleStepWasUpdated}
        />
      );
    }
    return (
      {
        Initializing: (
          <Grid container>{t("pipelineStatus.InitializingInfo")}</Grid>
        ),
      }[stepName] || null
    ); // Default to `null` if no match
  };

  React.useEffect(
    () =>
      // clean up WebSocket connection on component unmount
      () => {
        socket.disconnect();
      },
    []
  );

  return (
    <Grid
      sx={{
        overflowX: "hidden", // add this
        width: "100%",
      }}
    >
      <Grid
        id="processing-status-container"
        sx={{
          padding: theme.spacing(4), // instead of spacing(8, 8)

          overflowX: "hidden", // also add here
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("pipelineStatus.PageTitle")}
          <br />
        </Typography>
        <Typography variant="body2">
          {t("pipelineStatus.PageDescription")}
        </Typography>
        <br />

        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="flex-start"
          alignItems="start"
          sx={{
            width: "100%", // allow it to adapt to the parent
            maxWidth: "100%", // never overflow
            overflowX: "hidden", // hide any potential horizontal scroll
          }}
        >
          {isLoading || pipelineSteps.length === 0 ? (
            <Grid item xs={12}>
              <CircularProgress />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sx={{ width: "100%", opacity: (pipelineMode === "extend" && !stepWasUpdatedOnce)? 0 : 100 }}>
                  <Box sx={{ width: "100%"}}>
                  <Stepper
                    nonLinear
                    activeStep={pipelineSteps.indexOf(activeStep)}
                    alternativeLabel
                  >
                    {pipelineSteps.map((label, index) => {
                      const isActive = activeStep === label;
                      const isCompleted = Boolean(stepFinishStatus[index]);
                      const isClickable = true;

                      return (
                        <Step key={label} completed={isCompleted}>
                          <StepButton
                            onClick={handleStep(label)}
                            sx={{
                              cursor: isClickable ? "pointer" : "default",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": isClickable
                                ? {
                                    "& .MuiStepLabel-label": {
                                      color: theme.palette.primary.main,
                                      transform: "scale(1.02)",
                                    },
                                    "& .MuiStepIcon-root": {
                                      transform: "scale(1.1)",
                                    },
                                  }
                                : {},
                              ...(isActive && {
                                "& .MuiStepLabel-label": {
                                  color: theme.palette.primary.main,
                                  fontWeight: "bold",
                                  textDecoration: "underline",
                                  fontSize: "1.1rem",
                                },
                                "& .MuiStepIcon-root": {
                                  color: theme.palette.primary.main,
                                  fontSize: "1.5rem",
                                },
                              }),
                              ...(isCompleted &&
                                !isActive && {
                                  "& .MuiStepLabel-label": {
                                    color: theme.palette.success.main,
                                    fontWeight: 500,
                                  },
                                  "& .MuiStepIcon-root": {
                                    color: theme.palette.success.main,
                                  },
                                }),
                              ...(!isClickable && {
                                opacity: 0.6,
                                "& .MuiStepLabel-label": {
                                  color: theme.palette.text.disabled,
                                },
                              }),
                            }}
                          >
                            {label}
                          </StepButton>
                        </Step>
                      );
                    })}
                  </Stepper>

                  <Box
                    sx={{
                      textAlign: "center",
                      mt: 2,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                  >
                    {t("trainingStatus.NavigationMessage")}{" "}
                    <strong>{activeStep}</strong>
                  </Box>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                xl={12}
                sx={{
                  width: "100%",
                  px: { xs: 0, md: 2 },
                  opacity:
                    pipelineMode === "extend" && !stepWasUpdatedOnce ? 0 : 100,
                }}
              >
                {/* <Paper
                  elevation={3}
                  sx={{
                    width: "100%",
                    p: 4,
                    marginTop: theme.spacing(3),
                    background: `${theme.palette.primary.light}10`,
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "4px",
                      background: `${theme.palette.primary.main}`,
                    },
                  }}
                > */}
                {renderStepComponent(activeStep)}
                {/* </Paper> */}
              </Grid>
              <Grid
                item
                xs={12}
                xl={12}
                sx={{
                  width: "100%",
                  px: { xs: 0, md: 2 },
                  opacity:
                    pipelineMode === "extend" && !stepWasUpdatedOnce ? 0 : 100,
                }}
              >
                {Math.min(...stepFinishStatus) === 1 ? (
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      marginTop: theme.spacing(3),
                      background: `${theme.palette.success.light}10`,
                      border: `2px solid ${theme.palette.success.main}`,
                      borderRadius: 2,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: `${theme.palette.success.main}`,
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        mb: 3,
                        color: theme.palette.text.primary,
                        lineHeight: 1.6,
                      }}
                    >
                      {fromAnalyzer
                        ? t("trainingStatus.ViewingCompletedPipeline")
                        : t("trainingStatus.CompletedMessage")}
                    </Typography>

                    {/* Success Icon */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <CheckCircleIcon
                        sx={{
                          color: theme.palette.success.main,
                          fontSize: "2rem",
                          mr: 2,
                          animation: "pulse 2s infinite",
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.success.dark,
                          fontWeight: "bold",
                        }}
                      >
                        {fromAnalyzer
                          ? t("trainingStatus.CompletedMessage")
                          : t("trainingStatus.CompletedMessage")}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Button
                        onClick={handleFinish}
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<ArrowForwardIcon />}
                        sx={{
                          minWidth: 160,
                          fontWeight: "bold",
                          boxShadow: theme.shadows[4],
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: theme.shadows[8],
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        {t("trainingStatus.ProceedButton")}
                      </Button>
                    </Box>
                  </Paper>
                ) : null}
              </Grid>
              {pipelineMode === "extend" && !stepWasUpdatedOnce && (
                  <Grid item xs={12}>
                  <CircularProgress />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
